import { useEffect, useCallback } from "react";
import useStationOptions from "../hooks/useStationOptions";
import stationLabel from "../utils/stationLabel";
import { getDefaultStation } from "../services/defaultStation";
import {
    TextField,
    Button,
    Autocomplete,
    Stack,
    Paper,
    IconButton,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Grid,
    InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
    setDepartureStation,
    setArrivalStation,
    setJourneyDate,
    pickJourneyDate,
    setReturnDate,
    setTripType,
    setPassengers,
    clearJourneyDateAdjusted,
    MAX_PASSENGERS,
} from "../redux/slices/searchSlice";

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        bgcolor: "grey.50",
        height: 52,
    },
    "& .MuiInputLabel-root": {
        fontSize: "0.9rem",
    },
};

export default function TripSearchForm({ onSearch, variant = "default", isLoading = false }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { departureStation, arrivalStation, journeyDate, returnDate, passengers, tripType, journeyDateAdjusted } =
        useSelector((state) => state.search);

    // Persistovani datum iz prošlosti je pri rehidraciji pomeren na danas —
    // jednokratno obaveštenje, i to samo kad je istekli datum korisnik birao.
    useEffect(() => {
        if (!journeyDateAdjusted) return;
        enqueueSnackbar(t("home.dateAdjustedToToday"), { variant: "info" });
        dispatch(clearJourneyDateAdjusted());
    }, [journeyDateAdjusted, enqueueSnackbar, dispatch, t]);

    // Pri otvaranju forme datum nikad ne sme biti u prošlosti (lokalno vreme) —
    // ako nedostaje ili je pre danas, postavi na danas. Sigurnosna mreža nezavisna
    // od rehidracije (npr. app ostala otvorena preko ponoći).
    useEffect(() => {
        const today = dayjs().startOf("day");
        if (!journeyDate || dayjs(journeyDate).isBefore(today, "day")) {
            dispatch(setJourneyDate(today.format("YYYY-MM-DD")));
        }
    }, [journeyDate, dispatch]);

    // Podrazumevana polazna stanica (Podešavanja) popunjava SAMO prazan formular
    // pri prvom prikazu — nikad ne gazi ono što je korisnik već izabrao.
    useEffect(() => {
        if (departureStation) return;
        const saved = getDefaultStation();
        if (saved) dispatch(setDepartureStation(saved));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Stanice sa stvarnim polascima za izabrani datum; „Do" se sužava po
    // polaznoj. Promena datuma automatski povlači nove liste (keš u hooku).
    const dep = useStationOptions(journeyDate);
    const arr = useStationOptions(journeyDate, departureStation?.id ?? null);

    const handleSwapStations = useCallback(() => {
        const temp = departureStation;
        dispatch(setDepartureStation(arrivalStation));
        dispatch(setArrivalStation(temp));
    }, [dispatch, departureStation, arrivalStation]);

    const handleSearch = useCallback(() => {
        if (!departureStation || !arrivalStation || !journeyDate) return;
        // Ako je izabrani datum u prošlosti (npr. aplikacija ostala otvorena preko
        // ponoći), koristi današnji datum umesto prošlog.
        const today = dayjs().startOf("day");
        const safeDate = dayjs(journeyDate).isBefore(today, "day")
            ? today.format("YYYY-MM-DD")
            : journeyDate;
        if (safeDate !== journeyDate) dispatch(setJourneyDate(safeDate));
        const safeReturn =
            tripType === "return" && returnDate
                ? (dayjs(returnDate).isBefore(dayjs(safeDate), "day") ? safeDate : returnDate)
                : null;
        if (safeReturn && safeReturn !== returnDate) dispatch(setReturnDate(safeReturn));
        onSearch({
            departureStationId: departureStation.id,
            arrivalStationId: arrivalStation.id,
            from: safeDate,
            to: safeDate,
            returnDate: safeReturn,
            passengers,
        });
    }, [departureStation, arrivalStation, journeyDate, returnDate, tripType, passengers, onSearch, dispatch]);

    const isHero = variant === "hero";
    const isReturn = tripType === "return";

    return (
        <Paper
            elevation={isHero ? 16 : 3}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                maxWidth: 950,
                mx: "auto",
                bgcolor: isHero ? "rgba(255,255,255,0.95)" : "white",
                backdropFilter: isHero ? "blur(20px)" : "none",
                border: isHero ? "1px solid rgba(255,255,255,0.3)" : "none",
            }}
        >
            <ToggleButtonGroup
                value={tripType}
                exclusive
                onChange={(e, val) => val && dispatch(setTripType(val))}
                sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "center",
                    "& .MuiToggleButton-root": {
                        px: 3,
                        py: 0.7,
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "20px !important",
                        border: "none",
                        mx: 0.5,
                        bgcolor: "grey.100",
                        "&.Mui-selected": {
                            bgcolor: "primary.main",
                            color: "white",
                            "&:hover": { bgcolor: "primary.dark" },
                        },
                    },
                }}
            >
                <ToggleButton value="oneWay">{t("home.oneWay")}</ToggleButton>
                <ToggleButton value="return">{t("home.roundTrip")}</ToggleButton>
            </ToggleButtonGroup>

            {/* ROW 1: Stanice */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Autocomplete
                    fullWidth
                    options={dep.options}
                    getOptionLabel={stationLabel}
                    value={departureStation}
                    onChange={(_, value) => dispatch(setDepartureStation(value))}
                    onInputChange={(_, value, reason) => {
                        if (reason === "input") dep.setInput(value);
                    }}
                    loading={dep.loading}
                    filterOptions={dep.serverFiltered ? (x) => x : undefined}
                    noOptionsText={dep.serverFiltered && dep.input.trim().length < 2 ? t("home.minTwoChars") : t("home.noStations")}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t("home.fromShort")}
                            placeholder={t("home.stationPlaceholder")}
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <Icon icon="mdi:map-marker-radius" width={20} color="#1a1a2e" style={{ marginRight: 6 }} />
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                                endAdornment: (
                                    <>
                                        {dep.loading ? <CircularProgress size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={inputSx}
                        />
                    )}
                />

                <IconButton
                    onClick={handleSwapStations}
                    aria-label={t("common.swapStations")}
                    sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                        p: 0.8,
                        minWidth: 36,
                        minHeight: 36,
                        boxShadow: 2,
                        flexShrink: 0,
                        "&:hover": { bgcolor: "primary.dark", transform: "rotate(180deg)" },
                        transition: "all 0.3s",
                    }}
                >
                    <Icon icon="mdi:swap-horizontal" width={20} />
                </IconButton>

                <Autocomplete
                    fullWidth
                    options={arr.options}
                    getOptionLabel={stationLabel}
                    value={arrivalStation}
                    onChange={(_, value) => dispatch(setArrivalStation(value))}
                    onInputChange={(_, value, reason) => {
                        if (reason === "input") arr.setInput(value);
                    }}
                    loading={arr.loading}
                    filterOptions={arr.serverFiltered ? (x) => x : undefined}
                    noOptionsText={arr.serverFiltered && arr.input.trim().length < 2 ? t("home.minTwoChars") : t("home.noStations")}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t("home.toShort")}
                            placeholder={t("home.stationPlaceholder")}
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <Icon icon="mdi:map-marker-radius" width={20} color="#1a1a2e" style={{ marginRight: 6 }} />
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                                endAdornment: (
                                    <>
                                        {arr.loading ? <CircularProgress size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={inputSx}
                        />
                    )}
                />
            </Stack>

            {/* ROW 2: Datum + Putnici + Dugme */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={isReturn ? 3 : 4} md={isReturn ? 3 : 4}>
                        <DatePicker
                            label={t("results.departure")}
                            value={journeyDate ? dayjs(journeyDate) : null}
                            onChange={(val) =>
                                dispatch(pickJourneyDate(val ? val.format("YYYY-MM-DD") : null))
                            }
                            minDate={dayjs()}
                            format="DD.MM.YYYY"
                            slotProps={{
                                textField: {
                                    size: "small",
                                    fullWidth: true,
                                    sx: inputSx,
                                },
                            }}
                        />
                    </Grid>

                    {isReturn && (
                        <Grid item xs={12} sm={3} md={3}>
                            <DatePicker
                                label={t("booking.return")}
                                value={returnDate ? dayjs(returnDate) : null}
                                onChange={(val) =>
                                    dispatch(setReturnDate(val ? val.format("YYYY-MM-DD") : null))
                                }
                                minDate={journeyDate ? dayjs(journeyDate) : dayjs()}
                                format="DD.MM.YYYY"
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                        sx: inputSx,
                                    },
                                }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={6} sm={isReturn ? 2 : 3} md={isReturn ? 2 : 3}>
                        <TextField
                            label={t("home.passengers")}
                            type="number"
                            size="small"
                            fullWidth
                            value={passengers}
                            onChange={(e) => dispatch(setPassengers(e.target.value))}
                            InputProps={{
                                inputProps: { min: 1, max: MAX_PASSENGERS },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon="mdi:account-group" width={20} color="#637381" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={inputSx}
                        />
                    </Grid>

                    <Grid item xs={6} sm={isReturn ? 4 : 5} md={isReturn ? 4 : 5}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleSearch}
                            disabled={!departureStation || !arrivalStation || !journeyDate || isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Icon icon="mdi:magnify" width={22} />}
                            sx={{
                                height: 52,
                                fontSize: "1rem",
                                fontWeight: 700,
                                borderRadius: 2,
                                background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                                boxShadow: "0 6px 20px rgba(26,26,46,0.3)",
                                "&:hover": {
                                    background: "linear-gradient(135deg, #16213e 0%, #0f1525 100%)",
                                    boxShadow: "0 8px 28px rgba(26,26,46,0.4)",
                                    transform: "translateY(-1px)",
                                },
                                "&.Mui-disabled": {
                                    background: "grey.300",
                                },
                                transition: "all 0.2s",
                            }}
                        >
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Paper>
    );
}
